// User Authentication Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  profileImage?: string;
  createdAt: Date;
  updatedAt: Date;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

// Portfolio Types
export interface Project {
  id: string;
  title: string;
  slug: string;
  client: string;
  description: string;
  coverImage: string;
  images: string[];
  videos?: string[];
  category: string[];
  featured: boolean;
  testimonial?: Testimonial;
  completionDate: Date;
  createdAt: Date;
  updatedAt: Date;
  designer: string; // Designer who worked on this project
}

export enum ProjectCategory {
  PRODUCTION_DESIGN = 'Production Design',
  CONSULTING = 'Consulting',
  DRAFTING = 'Drafting',
  PRODUCTION_MANAGEMENT = 'Production Management',
  EQUIPMENT_RENTAL = 'Equipment Rental',
  STAGE_DESIGN = 'Stage Design',
  EVENT_PLANNING = 'Event Planning',
  STUDIO_DESIGN = 'Studio Design',
}

export interface Testimonial {
  id: string;
  author: string;
  position: string;
  company: string;
  content: string;
  projectId: string;
  createdAt: Date;
  updatedAt: Date;
}

// Service Types
export interface Service {
  id: string;
  title: string;
  slug: string;
  description: string;
  richDescription?: string; // HTML content from rich text editor
  icon: string; // Path to the icon or icon name
  features: string[];
  category: ServiceCategory;
  images: string[]; // Array of image URLs
  featuredImageIndex?: number; // Index of the featured image in the images array
  priceRange: string; // e.g. "$500 - $1,500"
  turnaroundTime: string; // e.g. "2-4 weeks"
  createdAt: Date;
  updatedAt: Date;
}

export enum ServiceCategory {
  PRODUCTION_DESIGN = 'Production Design',
  CONSULTING = 'Consulting',
  DRAFTING = 'Drafting',
  PRODUCTION_MANAGEMENT = 'Production Management',
  EQUIPMENT_RENTAL = 'Equipment Rental',
}

// Equipment Rental Types
export interface EquipmentItem {
  id: string;
  name: string;
  category: EquipmentCategory;
  description: string;
  specs: string[];
  dailyRate: number;
  weeklyRate: number;
  images: string[];
  available: boolean;
  featuredRental: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum EquipmentCategory {
  CAMERA = 'Camera',
  LENS = 'Lens',
  LIGHTING = 'Lighting',
  GRIP = 'Grip',
  AUDIO = 'Audio',
  ACCESSORIES = 'Accessories',
  OTHER = 'Other',
}

// Quote Request Types
export interface QuoteRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  projectType: ProjectCategory;
  projectDetails: string;
  budget?: string;
  timeline?: string;
  referralSource?: string;
  status: QuoteRequestStatus;
  createdAt: Date;
  updatedAt: Date;
}

export enum QuoteRequestStatus {
  PENDING = 'Pending',
  REVIEWING = 'Reviewing',
  QUOTED = 'Quoted',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  COMPLETED = 'Completed',
}

// CAD Navigation Types for the interactive homepage
export interface NavNode {
  id: string;
  title: string;
  path: string;
  position: {
    x: number;
    y: number;
    z: number;
  };
  connections: string[]; // IDs of connected nodes
  icon?: string;
  description?: string;
}

// Responsive Design Types
export enum BreakPoint {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
} 